import { FC, useEffect, useState } from 'react';
import { EntityData, RiskActivity } from '../types';
import { EmptyState, EmptyStateIcons, Icon, Icons, Info, SeverityLevel, Tooltip } from '@silverfort/sf-components';
import { getRiskIcon } from '../../../consts/Insight';
import moment from 'moment';
import { toFirstLetterUppercase } from '@silverfort-nodelibs/common/utils/common';
import { HTTP_OPTIONS, RISK_LEVELS, RISK_RANGE_FILTER } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../utils';
import jstz from 'jstimezonedetect';
import { HttpResponse } from '../../../api/types/Http';
import IdentityDataRiskGraph from '../Charts/IdentityDataRiskGraph';
import { usePricingModulesState } from '../../../context/contexts/PricingModuleContext';

const RISK_LINKS = {
    ISPM: '/ispm',
    ITDR: '/itdr'
};

const createRiskItem = (riskItem, metrics, link) => {
    const ruleMetricKey = Object.keys(metrics.insights).find((riskRule) => riskItem.riskRules.includes(riskRule));
    const timestamp = ruleMetricKey
        ? metrics.insights[ruleMetricKey].last_update_timestamp || metrics.insights[ruleMetricKey].init_timestamp
        : new Date();

    const summary = (
        <div className="flex p-2  justify-between items-center h-14">
            <div className="flex items-center gap-2">
                <Icon icon={getRiskIcon(riskItem.riskRules[0])} size={20} />
                <div className="h-full flex flex-col gap-2">
                    <div className="font-medium text-13 h-4 text-sf-gray-800 flex items-center gap-1">
                        {riskItem.entityTitle}{' '}
                        {riskItem.description && (
                            <div>
                                <Tooltip title={riskItem.description}>
                                    <Info />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center h-4 gap-1">
                        <div className="text-12 text-sf-gray-500">
                            {moment(timestamp * 1000).format('MMM DD, YYYY HH:mm')}
                        </div>
                        {riskItem.category && (
                            <>
                                <div className="text-12 text-sf-gray-500">|</div>
                                <div className="text-12 text-sf-gray-500 font-bold">{riskItem.category.title}</div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex w-fit items-center gap-2 px-2 h-6 border border-sf-gray-100 rounded-lg">
                <div className="flex items-center mb-2">
                    <div className="w-9">
                        <SeverityLevel severity={riskItem.severity || 1} />
                    </div>
                </div>
                <div className="text-12 text-sf-gray-800 flex items-center">
                    {toFirstLetterUppercase(
                        Object.values(RISK_LEVELS).find((riskLevel) => riskLevel.value === riskItem.severity)?.label ||
                            ''
                    )}
                </div>
            </div>
        </div>
    );
    return (
        <div className="w-full">
            <div
                className={`border rounded-lg !border-sf-gray-100 ${
                    link ? 'hover:border-sf-gray-300 cursor-pointer' : ''
                }`}
                onClick={
                    link
                        ? () => {
                              window.open(link + `?rule=${riskItem.riskRules[0]}`, '_blank');
                          }
                        : undefined
                }>
                {summary}
            </div>
        </div>
    );
};

const IdentityDataRiskTab: FC<{ entityData: EntityData }> = ({ entityData }) => {
    const [riskChanges, setRiskChanges] = useState<RiskActivity[] | undefined>();
    const [rangeFilter, setRangeFilter] = useState(RISK_RANGE_FILTER.LAST_7_DAYS.value);
    const { pricingModulesState } = usePricingModulesState();

    useEffect(() => {
        const getRiskChanges = async () => {
            const requestUrl = '/investigate/riskChanges';
            const params = {
                localTimeZone: encodeURIComponent(jstz.determine().name()),
                rangeFilter: rangeFilter,
                entityGuid: entityData.details.guid,
                entityId: entityData.id
            };

            try {
                const response = (await httpGet(requestUrl, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params: params }
                })) as unknown as HttpResponse<RiskActivity[]>;

                setRiskChanges(response.data);
            } catch (e) {
                console.error('Failed to load risk changes');
            }
        };

        getRiskChanges();
    }, [entityData.details.guid, entityData.id, rangeFilter]);

    const incidents = entityData.RiskReasons.filter((risk) => risk.type === 'Incident');
    const exposures = entityData.RiskReasons.filter((risk) => risk.type === 'Exposure');
    const abnormalities = entityData.RiskReasons.filter((risk) => risk.type === 'Abnormalities');
    const external = entityData.RiskReasons.filter((risk) => risk.type === 'External');

    let totalCount = external.length + abnormalities.length;

    if (pricingModulesState?.itdr?.anyEnabled) {
        totalCount += incidents.length;
    }

    if (pricingModulesState?.ispm?.anyEnabled) {
        totalCount += exposures.length;
    }

    if (!totalCount) {
        return (
            <div className="w-full flex justify-center items-center">
                <EmptyState title={'No Security Risks'} icon={EmptyStateIcons.NoItems} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex w-full gap-4">
                <div className="rounded-lg border border-sf-gray-100 w-[305px] h-fit max-h-50">
                    {pricingModulesState?.ispm?.anyEnabled && (
                        <div
                            className={`flex gap-1 items-center ${
                                pricingModulesState?.itdr?.anyEnabled ? 'border-b' : ''
                            } border-sf-gray-100 w-full h-25  px-4 text-13 font-bold text-sf-gray-800 font-sf-poppins`}>
                            <div>
                                <Icon icon={Icons.Warning} size={24} />
                            </div>
                            <div className="flex gap-1">
                                <span>Exposures</span> {exposures.length}
                            </div>
                        </div>
                    )}
                    {pricingModulesState?.itdr?.anyEnabled && (
                        <div
                            className={`w-full items-center h-25 px-4 text-13 font-bold text-sf-gray-800 font-sf-poppins`}>
                            <div className="flex gap-2  items-center h-full">
                                <div>
                                    <Icon icon={Icons.Target} size={24} />
                                </div>
                                <div className="flex flex-col">
                                    <div className="flex gap-1">
                                        <span>Incidents</span> {incidents.length}
                                    </div>
                                    <div className="font-normal text-12 text-sf-gray-500">Active Incidents</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="rounded-lg border border-sf-gray-100 h-50 w-full">
                    <IdentityDataRiskGraph
                        rangeFilter={rangeFilter}
                        setRangeFilter={setRangeFilter}
                        currentRiskLevel={entityData.RiskLevel}
                        data={riskChanges}
                    />
                </div>
            </div>
            <div className="w-full flex flex-col gap-6">
                {!!incidents.length && pricingModulesState?.itdr?.anyEnabled && (
                    <div className="flex flex-col gap-4 w-full">
                        <div className="text-12 text-sf-gray-500 font-medium">Incidents</div>
                        {incidents.map((incident) => createRiskItem(incident, entityData.metrics, RISK_LINKS.ITDR))}
                    </div>
                )}
                {!!exposures.length && pricingModulesState?.ispm?.anyEnabled && (
                    <div className="flex flex-col gap-4 w-full">
                        <div className="text-12 text-sf-gray-500 font-medium">Exposures</div>
                        {exposures.map((exp) => createRiskItem(exp, entityData.metrics, RISK_LINKS.ISPM))}
                    </div>
                )}
                {!!abnormalities.length && (
                    <div className="flex flex-col gap-4 w-full">
                        <div className="text-12 text-sf-gray-500 font-medium">Abnormalities</div>
                        {abnormalities.map((abnormality) => createRiskItem(abnormality, entityData.metrics, ''))}
                    </div>
                )}
                {!!external.length && (
                    <div className="flex flex-col gap-4 w-full">
                        <div className="text-12 text-sf-gray-500 font-medium">External</div>
                        {external.map((ext) => createRiskItem(ext, entityData.metrics, ''))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default IdentityDataRiskTab;
